import { useEffect, useRef, useState, useCallback } from 'react'
import { apiClient } from '../../api/client'
import type { ConversationDraft } from '../../api/schema'

/**
 * Autosaves a human reply as it is typed, so it survives a thread switch, a
 * folder change, a reload, or a laptop closing mid-visit (spec 2026-09-11-07).
 *
 * Storage is server-side, one row per (conversation, user) — it follows the
 * manager across devices — mirrored to `localStorage` so a keystroke survives
 * a reload even before the network round trip lands, or when the server is
 * briefly unreachable.
 *
 * This hook only ever saves what it is TOLD the current subject/body are — it
 * has no opinion on where that text came from. A human typing it and a
 * manager clicking "Use this draft" on a triage suggestion (spec
 * 2026-09-11-06, which funnels through the composer's own `setSubject`/
 * `setBody`) are indistinguishable here, which is exactly the point: once
 * applied, AI text is the manager's text.
 */

const DEBOUNCE_MS = 1500

export type DraftSaveStatus = 'idle' | 'saving' | 'saved'

type DraftContent = { subject: string; body: string }

function storageKey(org: string, conversationUid: string): string {
    return `conversation-draft:${org}:${conversationUid}`
}

function readLocal(org: string, conversationUid: string): (DraftContent & { updatedAt: number }) | null {
    try {
        const raw = localStorage.getItem(storageKey(org, conversationUid))
        if (!raw) return null
        const parsed = JSON.parse(raw)
        if (typeof parsed?.body !== 'string' || typeof parsed?.updatedAt !== 'number') return null

        return { subject: typeof parsed.subject === 'string' ? parsed.subject : '', body: parsed.body, updatedAt: parsed.updatedAt }
    } catch {
        return null
    }
}

function writeLocal(org: string, conversationUid: string, content: DraftContent): void {
    try {
        if (content.body.trim() === '') {
            localStorage.removeItem(storageKey(org, conversationUid))

            return
        }
        localStorage.setItem(storageKey(org, conversationUid), JSON.stringify({ ...content, updatedAt: Date.now() }))
    } catch {
        // Storage unavailable (private mode, quota) — never block typing over it.
    }
}

function clearLocal(org: string, conversationUid: string): void {
    try {
        localStorage.removeItem(storageKey(org, conversationUid))
    } catch {
        // ignore
    }
}

export function useConversationDraft({
    org,
    conversationUid,
    embeddedDraft,
    subject,
    body,
    onSaved,
}: {
    org: string
    conversationUid: string
    embeddedDraft: ConversationDraft | null | undefined
    subject: string
    body: string
    /**
     * Called after a save (or delete) actually lands. The conversation query
     * this hook was seeded from (`embeddedDraft`) is cached by TanStack Query
     * for several minutes — without this, switching away and back inside
     * that window would re-hydrate from the STALE pre-save conversation
     * object instead of the one this hook just wrote. Wire it to the same
     * invalidation a reply or any other edit already triggers.
     */
    onSaved?: () => void
}): {
    status: DraftSaveStatus
    savedAt: Date | null
    /**
     * Computes what the composer's fields should be seeded with for THIS
     * conversation: the embedded server draft, the local copy when it is
     * newer, or `defaultSubject`/'' when there is nothing saved at all. Call
     * this from the same effect that resets the fields on a conversation
     * change, and feed its result into `setSubject`/`setBody`.
     */
    hydrate: (defaultSubject: string) => DraftContent
    /** Saves (or deletes) right now, bypassing the debounce — for blur. */
    flushNow: () => void
    /** After a successful send: the server row is already gone (cleared in
     * the same transaction as the reply); this drops the local mirror and
     * resets the hint so it does not resurrect on the next open. */
    clearAfterSend: () => void
} {
    const [status, setStatus] = useState<DraftSaveStatus>('idle')
    const [savedAt, setSavedAt] = useState<Date | null>(null)

    // What the server currently holds, from this hook's point of view — null
    // means "no row". Compared against on every change so an unmodified
    // composer (or a re-render of the same text) never re-issues a request.
    const lastSavedRef = useRef<DraftContent | null>(null)
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    // Always the latest args, for the debounce timer and the unmount/switch
    // flush to read without depending on a stale render's closure.
    const latestRef = useRef({ org, conversationUid, subject, body })
    const onSavedRef = useRef(onSaved)
    // Runs after every commit (not during render, which refs must not be
    // written to) so the debounce timer and the unmount/switch flush below
    // always read the current values without depending on a stale closure.
    useEffect(() => {
        latestRef.current = { org, conversationUid, subject, body }
        onSavedRef.current = onSaved
    })

    const persist = useCallback((target: { org: string; conversationUid: string; subject: string; body: string }) => {
        const content: DraftContent = { subject: target.subject, body: target.body }
        const empty = content.body.trim() === ''
        const last = lastSavedRef.current

        if (empty) {
            if (last === null) return // already nothing saved — no request to make
        } else if (last !== null && last.subject === content.subject && last.body === content.body) {
            return // unchanged since the last successful save
        }

        setStatus('saving')
        const path = '/api/v1/organizations/{orgCode}/conversations/{conversationUid}/draft' as const
        const params = { params: { path: { orgCode: target.org, conversationUid: target.conversationUid } } }
        const request = empty
            ? apiClient.DELETE(path, params)
            : apiClient.PUT(path, { ...params, body: { subject: content.subject, body: content.body } })

        void request.then(({ error }) => {
            // A stale response for a thread we have since left (or switched
            // away and back to) must not overwrite the newer bookkeeping —
            // only apply it while it still answers the conversation it was
            // sent for.
            if (latestRef.current.conversationUid !== target.conversationUid) return
            if (error) {
                // Keep the local text and the pending state; the next change
                // (or the next flush) tries again. Never surface this as an
                // alarming error over a save that will simply retry.
                setStatus('idle')

                return
            }
            lastSavedRef.current = empty ? null : content
            // The server now holds this content — the local mirror was only
            // ever there to bridge the gap before this round trip landed, so
            // drop it. But NOT if a keystroke landed while the request was in
            // flight: that mirror is now the only copy of text the server has
            // never seen, and clearing it would lose exactly what this hook
            // exists to protect.
            const current = latestRef.current
            if (current.conversationUid === target.conversationUid && current.subject === content.subject && current.body === content.body) {
                clearLocal(target.org, target.conversationUid)
            }
            if (empty) {
                setStatus('idle')
                setSavedAt(null)
            } else {
                setStatus('saved')
                setSavedAt(new Date())
            }
            onSavedRef.current?.()
        })
    }, [])

    const flushNow = useCallback(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current)
            debounceRef.current = null
        }
        persist(latestRef.current)
    }, [persist])

    // Mirror to localStorage synchronously on every change, and (re)start the
    // debounce. Never on the same tick as a thread switch's cleanup below —
    // this effect only reacts to subject/body, not to conversationUid.
    useEffect(() => {
        writeLocal(org, conversationUid, { subject, body })
        if (debounceRef.current) clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => {
            debounceRef.current = null
            persist(latestRef.current)
        }, DEBOUNCE_MS)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subject, body])

    // Flush whatever is pending for the thread being LEFT — a switch to
    // another conversation or the composer unmounting entirely — so a
    // debounce that had not fired yet never loses the text. Keyed only on
    // conversationUid: it must not fire on every keystroke, only when the
    // thread identity changes.
    useEffect(() => {
        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current)
                debounceRef.current = null
                persist(latestRef.current)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversationUid])

    const hydrate = useCallback(
        (defaultSubject: string): DraftContent => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current)
                debounceRef.current = null
            }
            setStatus('idle')
            setSavedAt(null)

            const server: (DraftContent & { updatedAt: number }) | null = embeddedDraft
                ? { subject: embeddedDraft.subject ?? '', body: embeddedDraft.body, updatedAt: Date.parse(embeddedDraft.updated_at) }
                : null
            lastSavedRef.current = server ? { subject: server.subject, body: server.body } : null

            const local = readLocal(org, conversationUid)
            // Local wins only when it is BOTH newer and actually holds text —
            // a stale empty entry (leftover from a browser that never cleaned
            // up) must not resurrect a blank composer over a real server draft.
            if (local && local.body.trim() !== '' && (!server || local.updatedAt > server.updatedAt)) {
                return { subject: local.subject, body: local.body }
            }
            if (server) {
                return { subject: server.subject, body: server.body }
            }
            clearLocal(org, conversationUid)

            return { subject: defaultSubject, body: '' }
        },
        [org, conversationUid, embeddedDraft],
    )

    const clearAfterSend = useCallback(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current)
            debounceRef.current = null
        }
        lastSavedRef.current = null
        clearLocal(latestRef.current.org, latestRef.current.conversationUid)
        setStatus('idle')
        setSavedAt(null)
    }, [])

    return { status, savedAt, hydrate, flushNow, clearAfterSend }
}
